import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import {IconButton} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Column} from 'react-table';
import {Table, TablePagination, TableSort} from 'thermia-react-container';
import {Device, DeviceStatus} from '../../../../../types/device';
import {
  DeviceConflict,
  Registration,
  RegistrationConflictType,
  RegistrationStatusCode
} from '../../../../../types/registrationTypes';
import {extractDeviceArticleName} from '../../../../../validation/extractDeviceSerialNumber';
import {useIsAdmin} from "../../../../../hooks/useIsAdmin";

interface Props {
  label: string;
  devices: Device[];
  handleRemoveDeviceFromList: (id: Device['serialNumber']) => void;
  disabled?: boolean;
  registration: Registration;
  registrationIsCompleted: boolean;
}

interface DeviceDTOForTable {
  id: string;
  serialNumber: string;
  label: string;
  invalid: string;
  invalidDate: string;
}

const DeviceTable: FC<Props> = (
{ 
  devices,
  handleRemoveDeviceFromList, 
  disabled, 
  registration, 
  registrationIsCompleted,
}) => {
  const { t } = useTranslation('registrations');
  const theme = useTheme();
  const isAdmin = useIsAdmin();
  
  const fetchDataFunction = (
  pagination: TablePagination | null,
  search: string | null,
  sort: TableSort | null
  ): Promise<{ data: DeviceDTOForTable[]; totalCount: number }> => {
    return new Promise((resolve) => {
      const data = flattenedDevices;
      const totalCount = data.length;
      resolve({ data: data, totalCount });
    });
  };

  const [tableMethods] = useState({
    refreshData: () => Promise.resolve() // function will be set by the table
  });

  const refreshTableData = useCallback(async () => {
    await tableMethods.refreshData();
  }, [tableMethods]);

  useEffect(() => {
    refreshTableData();
  }, [devices, refreshTableData]);

  const isValid = useCallback(
  (device: Device): boolean => {
    if (registrationIsCompleted) {
      return device.status === DeviceStatus.installed;
    } else {
      if(!!registration.conflict && registration.conflictType === RegistrationConflictType.device) {
        return !(registration.conflict as DeviceConflict).includes(device.id);
      }
      return device.status !== DeviceStatus.installed;
    }
  },
  [registrationIsCompleted, registration.conflict, registration.conflictType]
  );

  const isValidDeliveryDate = useCallback(
  (device: Device): boolean => {
    // Valid if admin
    if(isAdmin) {
      return true;
    }
    // Valid if registration is completed
    if(registrationIsCompleted) {
      return true;
    }
    // Valid if dates are missing (nothing to compare)
    if(registration.location?.installationDate === undefined || device.deliverDate === undefined) {
      return true;
    }
    // Validate
    return new Date(device.deliverDate) <= new Date(registration.location.installationDate);
  },
  [registrationIsCompleted, registration.conflict, registration.conflictType, isAdmin]
  );

  const flattenedDevices: DeviceDTOForTable[] = useMemo(() => {
    return devices.map((device) => ({
      id: device.id,
      serialNumber: device.serialNumber,
      label: extractDeviceArticleName(device, t, registration.status === RegistrationStatusCode.Completed),
      invalid: isValid(device) ? 'false' : 'true',
      invalidDate: isValidDeliveryDate(device) ? 'false' : 'true',
    }));
  }, [devices, isValid, registration.status, t]);

  const isAnyDeviceInvalid = useMemo(() => flattenedDevices.some((device) => device.invalid === 'true'), [flattenedDevices]);
  const isAnyDeviceDeliveryDateInvalid = useMemo(() => flattenedDevices.some((device) => device.invalidDate === 'true'), [flattenedDevices]);

  const columns = useMemo<Column<DeviceDTOForTable>[]>(() => {
    return [
      {
        accessor: 'serialNumber',
        Header: t('registrations.general.serialNumber') ?? 'SerialNumber'
      },
      {
        accessor: 'label',
        Header: t('registrations.general.articleName') ?? 'Article name',
        Cell: (props) => (
        <>
          {props.row.original.label}
          {(props.row.original.invalid === 'true' || props.row.original.invalidDate === 'true') && <ErrorIcon sx={{ color: theme.palette.warning.main }} />}
        </>
        )
      },
      {
        accessor: 'id',
        header: '',
        Cell: (props) => (
        <>
          {!disabled && (
          <IconButton aria-label="delete" onClick={() => handleRemoveDeviceFromList(props.row.original.serialNumber)}>
            <DeleteIcon />
          </IconButton>
          )}
        </>
        )
      }
    ];
  }, [disabled, handleRemoveDeviceFromList, t, theme.palette.warning.main]);

  return (
  <>
    {isAnyDeviceInvalid && !disabled && (
    <p
    style={{
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.trueWhite.main,
      padding: theme.spacing(1)
    }}>
      {t('registrations.general.deviceInvalidInList')}
    </p>
    )}

    {isAnyDeviceDeliveryDateInvalid && !disabled && (
    <p
    style={{
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.trueWhite.main,
      padding: theme.spacing(1)
    }}>
      {t('registrations.general.errorDeviceInvalidDeliveryDate')}
    </p>
    )}

    <Table fetchData={fetchDataFunction} exposedMethods={tableMethods} columns={columns} />
  </>
  );
};

export default DeviceTable;
